import React, { FC, useMemo } from 'react';

import {
	EvSection,
	OptionType,
	TABLE_FILTERS_SR_SUMMARY,
	TEvFiltersPanelValues
} from '@evinced-private/ui-common';

import { MFA_SESSION_FILTERS_SECTION_ID } from 'data-automation-ids/MfaDashboardAutomationIds';
import { useMfaSessionFilterOptionsQuery } from 'src/apps/mfa/hooks/useMfaSessionFilterOptionsQuery';
import { useMfaSingleSessionFilters } from 'src/apps/mfa/hooks/useMfaSingleSessionFilters';
import { TMfaSessionIssuesFilterOptions } from 'src/apps/mfa/services/MfaDashboardServiceTypes';
import { ProductFiltersPanel } from 'src/common/components/pinned-date-range-filters-panel';
import { PLATFORM_APP_ID } from 'src/common/consts/dom-consts';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import {
	buildPanelInitialValues,
	mapPanelValuesToFilters,
	mapToPanelDefinitions
} from 'src/common/helpers/FiltersPanelHelper';

import './MfaSingleSessionFiltersSection.scss';

interface IMfaSingleSessionFiltersProps {
	sessionId: string;
	filters: IFilterOptions;
	onFilterChange: (tableId: string, filters: IFilterOptions) => void;
	tableId: string;
	ariaLabel: string;
}

export const MfaSingleSessionFiltersSection: FC<IMfaSingleSessionFiltersProps> = ({
	sessionId,
	filters,
	onFilterChange,
	tableId,
	ariaLabel
}) => {
	const filterOptionsQuery = useMfaSessionFilterOptionsQuery(sessionId);

	const { filterDefinitions, mergedFilterOptions } = useMfaSingleSessionFilters({
		allLabels: filterOptionsQuery.data?.scans ?? [],
		selectedLabels: ((filters as TMfaSessionIssuesFilterOptions)?.scans as OptionType[]) ?? [],
		filterOptions: filterOptionsQuery.data ?? {}
	});

	const panelDefinitions = useMemo(
		() => mapToPanelDefinitions(filterDefinitions),
		[filterDefinitions]
	);

	const initialValues = useMemo<TEvFiltersPanelValues>(
		() => buildPanelInitialValues(panelDefinitions, filters),
		[panelDefinitions, filters]
	);

	const handleApply = (appliedFilters: TEvFiltersPanelValues): void => {
		onFilterChange(tableId, mapPanelValuesToFilters(appliedFilters));
	};

	return (
		<EvSection
			ariaLabel={ariaLabel}
			ariaDescribedby={TABLE_FILTERS_SR_SUMMARY}
			ariaLive="polite"
			skipLinkId="filters-section"
			dataAutomationId={MFA_SESSION_FILTERS_SECTION_ID}
			className="mfa-single-session-filters-section"
		>
			<ProductFiltersPanel
				appElement={PLATFORM_APP_ID}
				onApply={handleApply}
				onReset={handleApply}
				initialValues={initialValues}
				filterOptions={mergedFilterOptions}
				filterDefinitions={panelDefinitions}
			/>
		</EvSection>
	);
};
